/**
 * The one place where our coordinate order becomes Leaflet's.
 *
 * Everything in `src` outside `map/` speaks GeoJSON: points are `[lon, lat]` and boxes are
 * `[west, south, east, north]`, because that is what the backend takes. Leaflet speaks
 * `[lat, lng]` and `[[south, west], [north, east]]`. Swapping the two silently is the single
 * most likely way to lose the imagery bounds guard — the box lands in another hemisphere,
 * the layer draws nothing or clips nothing, and the screen looks the same either way.
 *
 * So the swap lives here, in named functions, and nowhere else.
 */

import * as L from "leaflet";

/** GeoJSON `[lon, lat]` → Leaflet `[lat, lng]`. */
export const toLatLng = (point: [number, number]): L.LatLngTuple => [point[1], point[0]];

/** Leaflet `[lat, lng]` → GeoJSON `[lon, lat]`. */
export const toLonLat = (latlng: L.LatLng): [number, number] => [latlng.lng, latlng.lat];

/** `[west, south, east, north]` → `L.latLngBounds([[s, w], [n, e]])`. */
export const toLeafletBounds = (bbox: [number, number, number, number]): L.LatLngBounds =>
  L.latLngBounds([bbox[1], bbox[0]], [bbox[3], bbox[2]]);

/** A GeoJSON ring (`[lon, lat][]`) as Leaflet expects it. */
export const ringToLatLngs = (ring: number[][]): L.LatLngTuple[] =>
  ring.map((point) => [point[1] ?? 0, point[0] ?? 0] as L.LatLngTuple);
